import React, { useState } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import axios from 'axios';
import translate from '../../commonComponents/translate';
import withCSRF from '../../commonComponents/withCSRF';
import { compose } from '../../services/utilityFunctions';
import SignInContainer from '../signIn/container/SignInContainer';
import Title from '../../commonComponents/Title';
import FormInput from '../../commonComponents/forms/FormInput';
import Form from '../../commonComponents/forms/Form';
import TenantProp from '../../commonComponents/propTypes/tenantProp';
import Notifications from '../../services/notifications';
import UserProp from '../../commonComponents/propTypes/userProp';
import TermsAndPolicyCheckbox from '../../commonComponents/forms/TermsAndPolicyCheckbox';

const createInput = (t, field, state, onChange, type = 'text') => (
  <FormInput
    id={field}
    name={field}
    value={state[field]}
    onChange={value => onChange(field, value)}
    label={
      <div>
        {t(`complete.registration.${field}`)}
        <span className="text-danger"> *</span>
      </div>
    }
    type={type}
    disabled={field === 'email'}
    labelClassName="col-12"
    containerClassName="col-12"
    placeholder={t(`complete.registration.${field}.placeholder`)}
    groupProps={{ className: 'row' }}
    inputProps={{ autoComplete: field === 'password' ? 'new-password' : 'off' }}
  />
);

const CompleteRegistration = ({
  t,
  _csrf,
  logo,
  theme,
  tenant,
  redirectPath,
  inviteToken,
  user,
  userOrganizations,
  errorRegistration,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { email, organization } = user || {};

  const validationRules = {
    email: { required: true, email: true },
    firstName: { required: true },
    lastName: { required: true },
    password: { required: true, minlength: 6 },
    retypedPassword: {
      required: true,
      equalTo: '#password',
    },
  };

  // Checkbox handling
  const [checkbox, setCheckbox] = useState(false);
  const handleCheckbox = () => {
    setCheckbox(!checkbox);
  };

  // Form state handling
  const [form, setForm] = useState({
    email: email || null,
    password: '',
    retypedPassword: '',
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
  });

  const handleChange = (field, value) => {
    setForm(prevState => ({
      ...prevState,
      [field]: value,
    }));
  };
  const handleSignIn = async params => {
    try {
      const organizationIdentifier =
        organization && organization.identifierCode;
      const result = await axios.post(
        '/signin',
        {
          email: params.email,
          password: params.password,
          organizationIdentifier,
        },
        {
          headers: {
            'Accept-Version': '1.0.0',
            'X-Gamification-API': 'Organization',
            'X-CSRF-Token': _csrf,
            _csrf,
          },
        },
      );
      console.log('*handleSignIn RESULT', result);
      window.location.href = redirectPath || '/';
      setIsLoading(false);
      return true;
    } catch (err) {
      console.log('*handleSignIn', err);
      Notifications.showErrorFromRequest(err, 'signIn.error');
      setIsLoading(false);
      return false;
    }
  };

  const handleRegistration = async params => {
    try {
      const url = `/user/registration/complete/${inviteToken}`;
      if (!params.firstName || params?.firstName?.length === 0) {
        Notifications.showNotificationError(t('error'), t('error.firstName'));
        setForm(prevState => ({ ...prevState, firstName: '' }));
        return false;
      }
      if (!params.lastName || params?.lastName?.length === 0) {
        Notifications.showNotificationError(t('error'), t('error.firstName'));
        setForm(prevState => ({ ...prevState, lastName: '' }));
        return false;
      }
      const result = await axios.put(url, params, {
        headers: {
          'Accept-Version': '1.0.0',
          'X-Gamification-API': 'Organization',
          'X-CSRF-Token': _csrf,
          _csrf,
        },
      });
      console.log('*handleRegistration RESULT', result);
      const resultSignIn = await handleSignIn(params);
      console.log('*RESULT SIGN IN', resultSignIn);
      return true;
    } catch (err) {
      console.log('*handleRegistration', err);
      Notifications.showErrorFromRequest(err, 'complete.registration.error');
      setIsLoading(false);
      return false;
    }
  };
  const handleFormSubmission = async () => {
    setIsLoading(true);
    const params = {
      ...form,
      username: `${form.firstName} ${form.lastName}`,
    };
    const result = await handleRegistration(params);
    console.log('*handleFormSubmission', result);
    setIsLoading(false);
  };

  // Styles
  const submitButtonClasses = cx(
    'btn',
    'btn-dynamic',
    'm-btn',
    'm-btn--pill',
    'm-btn--custom',
    'm-btn--air',
    'm-login__btn',
    { 'm-loader': isLoading },
    { 'm-loader--right': isLoading },
    { 'm-loader--light': isLoading },
  );

  return (
    <Title title={t('complete.registration.title')}>
      <div
        className="m-grid m-grid--hor m-grid--root m-page complete-registration-wrapper"
        style={{ height: '100vh' }}
      >
        <SignInContainer theme={theme} logo={logo}>
          <h4>{t('complete.registration.title')}</h4>
          {!errorRegistration ? (
            <Form
              id="registrationForm"
              className="m-login__form m-form"
              submitMethod={handleFormSubmission}
              validationRules={validationRules}
            >
              {submitHandler => (
                <>
                  {userOrganizations && userOrganizations.length > 1 && (
                    <div className="info-block">
                      <span>{`${t(
                        'complete_registration_organizations_info',
                      )}`}</span>
                      <br />
                      <span>
                        {t(
                          'complete_registration_organizations_number',
                          userOrganizations.length,
                        )}
                      </span>
                    </div>
                  )}
                  {createInput(t, 'email', form, handleChange)}
                  {createInput(t, 'firstName', form, handleChange)}
                  {createInput(t, 'lastName', form, handleChange)}
                  {createInput(t, 'password', form, handleChange, 'password')}
                  {createInput(
                    t,
                    'retypedPassword',
                    form,
                    handleChange,
                    'password',
                  )}
                  <TermsAndPolicyCheckbox
                    checkboxValue={checkbox}
                    handleCheckbox={handleCheckbox}
                    tenant={tenant}
                  />
                  <div className="m-login__form-action">
                    <button
                      type="submit"
                      className={submitButtonClasses}
                      disabled={isLoading || !checkbox}
                      onClick={submitHandler}
                    >
                      {t('submit')}
                    </button>
                  </div>
                </>
              )}
            </Form>
          ) : (
            <div className="error-block">{t(`${errorRegistration.error}`)}</div>
          )}
        </SignInContainer>
      </div>
    </Title>
  );
};

CompleteRegistration.propTypes = {
  t: PropTypes.func.isRequired,
  tenant: PropTypes.shape(TenantProp.propType).isRequired,
  _csrf: PropTypes.string.isRequired,
  inviteToken: PropTypes.string.isRequired,
  user: PropTypes.shape(UserProp.propTypes).isRequired,
  userOrganizations: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string,
      name: PropTypes.string,
      identifierCode: PropTypes.string,
      isActive: PropTypes.bool,
    }),
  ).isRequired,
  redirectPath: PropTypes.string,
  errorRegistration: PropTypes.shape({
    error: PropTypes.string,
  }),
  /** Selected metronic theme, from the Config */
  theme: PropTypes.string.isRequired,
  logo: PropTypes.string,
};

CompleteRegistration.defaultProps = {
  logo: null,
  redirectPath: '',
  errorRegistration: null,
};

export default compose(translate, withCSRF)(CompleteRegistration);
